import React, { useState } from 'react';
import { Box, Typography, Card, MenuItem, Select, FormControl, InputLabel, CircularProgress, TextField, Button, Alert, Fab } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';

const PROMPT_OPTIONS = [
  { label: 'CONVERSATION PROMPT', value: 'CONVERSATION PROMPT' },
  { label: 'REPORT GENERATION PROMPT', value: 'REPORT GENERATION PROMPT' },
];

export default function PromptCustomization() {
  const [promptType, setPromptType] = useState('');
  const [prompt, setPrompt] = useState('');
  const [originalPrompt, setOriginalPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = async (event) => {
    const value = event.target.value;
    setPromptType(value);
    setPrompt('');
    setOriginalPrompt('');
    setError('');
    setSuccess(false);
    setLoading(true);
    try {
      const res = await fetch(`https://api.uxhq.com/prompts?prompt_type=${encodeURIComponent(value)}`);
      if (!res.ok) throw new Error('Failed to fetch prompt');
      const data = await res.json();
      setPrompt(data.prompt || '');
      setOriginalPrompt(data.prompt || '');
    } catch (err) {
      setError('Could not fetch prompt.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess(false);
    try {
      const res = await fetch('https://api.uxhq.com/prompts', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt_type: promptType, prompt }),
      });
      if (!res.ok) throw new Error('Failed to update prompt');
      const data = await res.json();
      setPrompt(data.prompt || '');
      setOriginalPrompt(data.prompt || '');
      setSuccess(true);
    } catch (err) {
      setError('Could not update prompt.');
    } finally {
      setSaving(false);
    }
  };

  const isEdited = prompt !== originalPrompt && promptType;

  return (
    <Box sx={{ flexGrow: 1, width: '100%', p: { xs: 1, sm: 2, md: 4 }, mt: 10, background: '#f8f9fb', minHeight: '100vh' }}>
      <Card sx={{ width: '100%', p: 4, borderRadius: 5, boxShadow: 'none', border: '2px solidrgb(255, 255, 255)', background: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
        <Typography variant="h5" fontWeight={700} mb={2}>
          Prompt Customization
        </Typography>
        <FormControl sx={{ minWidth: 320 }}>
          <InputLabel id="prompt-type-label">Select Prompt Type</InputLabel>
          <Select
            labelId="prompt-type-label"
            value={promptType}
            label="Select Prompt Type"
            onChange={handleChange}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#00ff00',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#00ff00',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#00ff00',
              },
              '& .MuiSelect-icon': {
                color: '#black',
              },
            }}
          >
            {PROMPT_OPTIONS.map((opt) => (
              <MenuItem
                key={opt.value}
                value={opt.value}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: 'white', // light green background
                    color: 'black',
                  },
                  '&.Mui-selected:hover': {
                    backgroundColor: 'white',
                  },
                }}
              >
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {loading && <CircularProgress sx={{ mt: 2 }} />}
        {error && <Alert severity="error">{error}</Alert>}
        {success && <Alert severity="success">Prompt updated successfully!</Alert>}
        {promptType && !loading && !error && (
          <Box sx={{ width: '100%', position: 'relative' }}>
            <TextField
              multiline
              minRows={6}
              maxRows={20}
              value={prompt}
              onChange={e => { setPrompt(e.target.value); setSuccess(false); }}
              fullWidth
              sx={{
                mt: 2,
                borderRadius: 2,
                overflow: 'auto',
                '& .MuiInputBase-root': {
                  alignItems: 'flex-start',
                },
                '& textarea': {
                  overflowY: 'auto',
                  resize: 'vertical',
                },
              }}
            />
            {isEdited && (
              <Fab
                color="primary"
                aria-label="save"
                onClick={handleSave}
                disabled={saving}
                sx={{
                  position: 'absolute',
                  right: 24,
                  bottom: 24,
                  zIndex: 20,
                  boxShadow: 3,
                }}
              >
                <SaveIcon />
              </Fab>
            )}
          </Box>
        )}
      </Card>
    </Box>
  );
}